// Speed thresholds
export const SPEED_THRESHOLDS = {
  connection: {
    fast: 500,
    slow: 1500,
    verySlow: 3000,
  },
  download: {
    good: 50,
    slow: 20,
  },
};

// Test types
export type TestStatus = "pending" | "running" | "passed" | "failed";

export interface TestResult {
  id: string;
  name: string;
  description: string;
  status: TestStatus;
  message?: string;
  details?: Record<string, unknown>;
}

export interface BrowserInfo {
  browserName: string;
  browserVersion: string;
  isRecent: boolean;
}

// Browser detection
export function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent;

  if (ua.includes("Firefox")) {
    const match = ua.match(/Firefox\/(\d+)/);
    return { browserName: "Firefox", browserVersion: match?.[1] || "", isRecent: checkVersion(match?.[1]) };
  }
  if (ua.includes("Edg/")) {
    const match = ua.match(/Edg\/(\d+)/);
    return { browserName: "Edge", browserVersion: match?.[1] || "", isRecent: checkVersion(match?.[1]) };
  }
  if (ua.includes("Chrome")) {
    const match = ua.match(/Chrome\/(\d+)/);
    return { browserName: "Chrome", browserVersion: match?.[1] || "", isRecent: checkVersion(match?.[1]) };
  }
  if (ua.includes("Safari") && !ua.includes("Chrome")) {
    const match = ua.match(/Version\/(\d+)/);
    return { browserName: "Safari", browserVersion: match?.[1] || "", isRecent: checkVersion(match?.[1]) };
  }

  return { browserName: "Unknown", browserVersion: "", isRecent: true };
}

function checkVersion(version?: string): boolean {
  return version ? parseInt(version) >= 120 : true;
}

// Download test service
export class DownloadTester {
  private testUrl: string;

  constructor(testUrl: string) {
    this.testUrl = testUrl;
  }

  async runAllTests(
    checkBrowser: boolean,
    onProgress: (progress: number | null) => void,
    onUpdate: (id: string, status: TestStatus, message?: string, details?: Record<string, unknown>) => void
  ): Promise<TestResult[]> {
    const results: TestResult[] = [];

    // Test 1: Browser check
    if (checkBrowser) {
      await delay(500);
      const info = detectBrowser();
      onUpdate("browser", "passed", `You're using ${info.browserName} ${info.browserVersion}, which is up-to-date`);
      results.push({ id: "browser", name: "Browser Version", description: "", status: "passed", message: `You're using ${info.browserName} ${info.browserVersion}, which is up-to-date` });
    }

    // Test 2: Connection
    onUpdate("connection", "running");
    const connectionResult = await this.testConnection();
    results.push({ id: "connection", name: "Connection Test", description: "", status: connectionResult.status, message: connectionResult.message, details: connectionResult.details });
    onUpdate("connection", connectionResult.status, connectionResult.message, connectionResult.details);

    // Test 3 & 4: Download Speed & Integrity
    onUpdate("speed", "running");
    onUpdate("integrity", "running");
    const downloadResult = await this.testDownload(onProgress);
    results.push({ id: "speed", name: "Download Speed", description: "", status: downloadResult.status, message: downloadResult.speedMessage, details: downloadResult.details });
    results.push({ id: "integrity", name: "File Integrity", description: "", status: downloadResult.status, message: downloadResult.integrityMessage });
    onUpdate("speed", downloadResult.status, downloadResult.speedMessage, downloadResult.details);
    onUpdate("integrity", downloadResult.status, downloadResult.integrityMessage);

    return results;
  }

  private async testConnection(): Promise<{ status: TestStatus; message: string; details?: Record<string, unknown> }> {
    const start = performance.now();
    try {
      const response = await fetch(this.testUrl, { method: "HEAD", headers: { "Cache-Control": "no-cache" } });
      const time = performance.now() - start;

      if (!response.ok) {
        return { status: "failed", message: `Server responded with error: ${response.status} ${response.statusText}` };
      }

      const grade = this.getConnectionGrade(time);
      return {
        status: "passed",
        message: grade.slow
          ? `Connection speed: ${grade.label}. Some delays may occur when loading videos.`
          : `Connection speed: ${grade.label}. Videos will load quickly.`,
        details: { speedGrade: grade.label, timeMs: time.toFixed(0) },
      };
    } catch (error) {
      const isCors = error instanceof Error && error.message.includes("Failed to fetch");
      return {
        status: "failed",
        message: "Could not connect to server. Please check your network connection and try again.",
        details: isCors ? { error: "CORS error: Server is not configured to allow requests from this origin." } : { error: String(error) },
      };
    }
  }

  private getConnectionGrade(timeMs: number): { label: string; slow: boolean } {
    if (timeMs > SPEED_THRESHOLDS.connection.verySlow) return { label: "Very Slow", slow: true };
    if (timeMs > SPEED_THRESHOLDS.connection.slow) return { label: "Slow", slow: true };
    if (timeMs > SPEED_THRESHOLDS.connection.fast) return { label: "Good", slow: false };
    return { label: "Fast", slow: false };
  }

  private async testDownload(onProgress: (progress: number | null) => void): Promise<{
    status: TestStatus;
    speedMessage: string;
    integrityMessage: string;
    details?: Record<string, unknown>;
  }> {
    try {
      // Get file size
      const headRes = await fetch(this.testUrl, { method: "HEAD", headers: { "Cache-Control": "no-cache" } });
      const totalBytes = parseInt(headRes.headers.get("content-length") || "0");

      // Download with progress
      const res = await fetch(this.testUrl, { headers: { "Cache-Control": "no-cache" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body?.getReader();
      if (!reader) throw new Error("ReadableStream not supported");

      onProgress(0);
      let downloadedBytes = 0;
      const startTime = performance.now();
      let lastPercent = -1;
      let lastUpdateTime = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        downloadedBytes += value.length;

        const now = Date.now();
        if (totalBytes > 0 && now - lastUpdateTime > 200) {
          const percent = Math.round((downloadedBytes / totalBytes) * 100);
          if (percent !== lastPercent) {
            lastPercent = percent;
            lastUpdateTime = now;
            onProgress(percent);
          }
        }
      }

      onProgress(100);

      const downloadTime = performance.now() - startTime;
      const sizeMB = (downloadedBytes / 1024 / 1024).toFixed(1);
      const speedMbps = ((downloadedBytes * 8) / downloadTime / 1000);
      const speedGrade = this.getSpeedGrade(speedMbps);
      const isSlow = speedMbps < SPEED_THRESHOLDS.download.good;

      onProgress(null);

      return {
        status: "passed",
        speedMessage: isSlow
          ? `Download speed is ${speedGrade} (${speedMbps.toFixed(0)} Mbps). Videos may take longer to load.`
          : `Download speed is Good (${speedMbps.toFixed(0)} Mbps). Videos will load quickly.`,
        integrityMessage: `File downloaded successfully (${sizeMB} MB).`,
        details: { speedMbps: speedMbps.toFixed(0), speedGrade, sizeMB },
      };
    } catch (error) {
      onProgress(null);
      const isCors = error instanceof Error && error.message.includes("Failed to fetch");
      return {
        status: "failed",
        speedMessage: "Could not download test file. Please check your network connection and try again.",
        integrityMessage: "Could not download test file. Please check your network connection and try again.",
        details: isCors ? { error: "CORS error" } : { error: String(error) },
      };
    }
  }

  private getSpeedGrade(speedMbps: number): string {
    if (speedMbps > SPEED_THRESHOLDS.download.good) return "Good";
    if (speedMbps > SPEED_THRESHOLDS.download.slow) return "Slow";
    return "Very Slow";
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
